/**
 * How a declared entity's rows land when a run reads it incrementally, from its `mergeConfig`
 * (seed `entities[]`, stored as `connector_entities.merge_config`).
 *
 * Two modes are acted on, the two the connector images write (skyConnect
 * `source-mouseflow/source_mouseflow/contract/metadata.py` `load_mode_for`):
 *
 * - `wipe_and_load`: the table is replaced whole on every run (a reference table).
 * - `replace_where`: an incremental run re-reads a window of days and replaces the rows whose
 *   `replaceWhereColumn` falls in that window; a full run replaces the table whole.
 *
 * Anything else (no config, another mode, a window with no column) is `null`: nothing the load
 * can check an image's manifest against, and never read as "replace everything".
 */

export type DeclaredLoad =
  { loadMode: "wipe_and_load" } | { loadMode: "replace_where"; replaceWhereColumn: string };

export function declaredLoadOf(mergeConfig: unknown): DeclaredLoad | null {
  if (typeof mergeConfig !== "object" || mergeConfig === null || Array.isArray(mergeConfig)) {
    return null;
  }
  const config = mergeConfig as Record<string, unknown>;
  if (config.loadMode === "wipe_and_load") return { loadMode: "wipe_and_load" };
  if (config.loadMode === "replace_where") {
    const column =
      typeof config.replaceWhereColumn === "string" ? config.replaceWhereColumn.trim() : "";
    return column ? { loadMode: "replace_where", replaceWhereColumn: column } : null;
  }
  return null;
}

/**
 * True when a connector's declared entities say, every one of them, how an incremental read
 * lands, and at least one replaces a window. Such a connector can offer incremental refresh with
 * no per-entity watermark (FR-32): the load replaces each window table's window and each
 * reference table whole, so no delta ever overwrites a table.
 */
export function declaresWindowReplace(entities: readonly { mergeConfig?: unknown }[]): boolean {
  if (entities.length === 0) return false;
  const modes = entities.map((entity) => declaredLoadOf(entity.mergeConfig));
  if (modes.some((mode) => mode === null)) return false;
  return modes.some((mode) => mode?.loadMode === "replace_where");
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * #2428: the `mergeConfig` a hand-named connector gives every entity a person adds
 * (`entitySelection.addEntityMergeConfig`, GA4's `replace_where` on `_data_date`), or null when it
 * declares none or is not hand-named.
 */
export function addEntityMergeConfigOf(entitySelection: unknown): unknown {
  const selection = recordOf(entitySelection);
  if (selection?.supportsAddEntity !== true) return null;
  return selection.addEntityMergeConfig ?? null;
}

/**
 * #2428: what a definition says about how its tables land: the declared entities' configs, or,
 * for a hand-named connector that declares one, its `addEntityMergeConfig` as the one config
 * every added entity takes. `declaresWindowReplace` reads this list in the seed validator,
 * `ConnectorDefinitionService.wholeDayConnectors` and the tunables update guard alike.
 */
export function declaredLoadsOf(definition: {
  entities?: readonly unknown[] | null;
  entitySelection?: unknown;
}): { mergeConfig?: unknown }[] {
  const entities = Array.isArray(definition.entities) ? definition.entities : [];
  if (entities.length > 0) {
    return entities.map((entity) => ({ mergeConfig: recordOf(entity)?.mergeConfig }));
  }
  const added = addEntityMergeConfigOf(definition.entitySelection);
  return added === null ? [] : [{ mergeConfig: added }];
}
